import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, CheckCircle, RefreshCw, Smartphone, 
  Search, ArrowRight, UploadCloud, Database,
  CreditCard, AlertCircle, ScanLine, Brain, User, 
  FileCheck, Printer
} from 'lucide-react';
import { Badge } from '../../ui/badge';

const BenefitFormsSlide = () => {
  const [processingStep, setProcessingStep] = useState(0);
  const [activeForm, setActiveForm] = useState(mockForms[0]);

  // Simulate the processing loop for the "Live" feel
  useEffect(() => {
    const interval = setInterval(() => {
      setProcessingStep((prev) => {
        if (prev === 4) {
            // Move to next form after a pause
            const nextIndex = (mockForms.findIndex(f => f.id === activeForm.id) + 1) % mockForms.length;
            setActiveForm(mockForms[nextIndex]);
            return 0;
        }
        return prev + 1;
      });
    }, 2000); // 2 seconds per step
    return () => clearInterval(interval);
  }, [activeForm]);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden text-slate-900">
        {/* Abstract Background */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/50 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-indigo-100/50 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

        {/* Floating Agent Header (Top Left) */}
        <div className="absolute top-8 left-8 lg:left-12 z-50 flex items-center gap-4 bg-white/40 backdrop-blur-md p-2 pr-6 rounded-full border border-white/20 shadow-lg">
            <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white shadow-sm">
                 <img src="/assets/amanda.png" alt="Amanda" className="w-full h-full object-cover" />
            </div>
            <div className="text-left">
                <h3 className="text-lg font-bold text-slate-900 leading-none">Amanda</h3>
                <p className="text-xs text-blue-600 font-bold uppercase tracking-wide">Insurance Verification</p>
            </div>
        </div>

        <div className="flex flex-row h-full w-full max-w-[1600px] gap-8 relative z-10 pt-0">
            {/* Left Side: Context */}
            <div className="w-1/3 flex flex-col justify-center space-y-6">
                
                <h2 className="text-4xl lg:text-5xl font-bold leading-tight text-slate-900">
                    Amanda can fill out your <span className="text-blue-600">custom benefit forms</span> automatically.
                </h2>
                
                <p className="text-lg text-slate-600 leading-relaxed">
                    Stop manually filling out benefit sheets. Upload your practice's unique form templates, and Amanda will fill them with verified data and place them directly into the patient's chart in Asha PMS.
                </p>

            </div>

            {/* Right Side: Dashboard Mockup */}
            <div className="w-2/3 h-full flex items-center">
                <motion.div 
                    initial={{ scale: 0.95, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    className="w-full h-[650px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
                >
                    {/* Browser Toolbar */}
                    <div className="h-12 bg-slate-50 border-b border-slate-200 flex items-center px-4 space-x-4 flex-shrink-0">
                        <div className="flex space-x-2">
                            <div className="w-3 h-3 rounded-full bg-red-400/80" />
                            <div className="w-3 h-3 rounded-full bg-yellow-400/80" />
                            <div className="w-3 h-3 rounded-full bg-green-400/80" />
                        </div>
                        <div className="flex-1 bg-white border border-slate-200 rounded-md h-8 flex items-center px-3 text-xs text-slate-400 font-mono shadow-sm">
                            <Brain className="w-3 h-3 mr-2 text-blue-500" />
                            asha.ai/verification/forms
                        </div>
                    </div>

                    {/* App Content */}
                    <div className="flex-1 flex overflow-hidden">
                        {/* Main Content: Split View */}
                        <div className="flex-1 bg-white flex flex-col relative">
                            
                            {/* Header */}
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-3">
                                        {activeForm.templateName}
                                        <ProcessingStatusBadge step={processingStep} />
                                    </h2>
                                    <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
                                        <User className="w-3 h-3" /> {activeForm.patient} • {activeForm.insurance}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">Appointment</p>
                                    <p className="text-sm font-bold text-slate-700">{activeForm.appointmentTime}</p>
                                </div>
                            </div>

                            {/* Two-Column Layout */}
                            <div className="flex-1 flex overflow-hidden relative">
                                
                                {/* Overlay for Success State */}
                                <AnimatePresence>
                                    {processingStep === 4 && (
                                        <motion.div 
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 z-30 bg-white/80 backdrop-blur-sm flex items-center justify-center"
                                        >
                                            <div className="text-center transform scale-110">
                                                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl shadow-green-200">
                                                    <CheckCircle className="w-10 h-10 text-green-600" />
                                                </div>
                                                <h3 className="text-2xl font-bold text-slate-900 mb-2">Saved to PMS</h3>
                                                <p className="text-slate-500">Form attached to {activeForm.patient}'s chart</p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Left: The Form Template (Visual) */}
                                <div className="w-1/2 p-8 border-r border-slate-100 overflow-y-auto bg-slate-50/30">
                                    <div className="flex items-center justify-between mb-6">
                                        <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                                            <Printer className="w-4 h-4" /> Form Template
                                        </h4>
                                        <Badge variant="outline" className="bg-white text-slate-500 border-slate-200 text-[10px]">
                                            Office_Breakdown_v2.pdf
                                        </Badge>
                                    </div>
                                    
                                    {/* The Paper Form Look */}
                                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 min-h-[500px] relative">
                                        {/* Header of Form */}
                                        <div className="flex justify-between items-start border-b-2 border-slate-800 pb-4 mb-6">
                                            <div>
                                                <div className="text-xl font-serif font-bold text-slate-900">DENTAL BENEFITS</div>
                                                <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">Insurance Breakdown</div>
                                            </div>
                                            <div className="w-12 h-12 border border-slate-200 bg-slate-50" />
                                        </div>

                                        {/* Scan Effect Overlay */}
                                        {processingStep === 1 && (
                                            <motion.div 
                                                className="absolute top-0 left-0 w-full h-1 bg-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.5)] z-10"
                                                animate={{ top: ["0%", "100%", "0%"] }}
                                                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                            />
                                        )}

                                        {/* Form Fields */}
                                        <div className="space-y-6 font-mono text-sm">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-1">
                                                    <label className="text-[10px] text-slate-400 uppercase">Patient Name</label>
                                                    <div className="border-b border-slate-300 h-6 flex items-end pb-0.5 text-slate-900 font-bold">
                                                        {processingStep > 0 && (
                                                            <motion.span initial={{opacity:0}} animate={{opacity:1}}>{activeForm.patient}</motion.span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="space-y-1">
                                                    <label className="text-[10px] text-slate-400 uppercase">DOB</label>
                                                    <div className="border-b border-slate-300 h-6 flex items-end pb-0.5 text-slate-900">
                                                        {processingStep > 0 && (
                                                            <motion.span initial={{opacity:0}} animate={{opacity:1}}>{activeForm.dob}</motion.span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-4 pt-4">
                                                {activeForm.fields.map((field, i) => (
                                                    <div key={i} className="flex justify-between items-end">
                                                        <span className="text-slate-600 w-1/3">{field.label}:</span>
                                                        <div className="flex-1 border-b border-slate-300 h-6 ml-2 flex items-end pb-0.5 px-2 relative">
                                                            {/* Handwriting / Typewriter Effect */}
                                                            {processingStep >= 3 && (
                                                                <motion.span 
                                                                    initial={{ opacity: 0, width: 0 }}
                                                                    animate={{ opacity: 1, width: '100%' }}
                                                                    transition={{ duration: 0.5, delay: i * 0.2 }}
                                                                    className="text-blue-700 font-bold font-handwriting block whitespace-nowrap overflow-hidden"
                                                                >
                                                                    {field.value}
                                                                </motion.span>
                                                            )}
                                                            {/* Checkmark when done */}
                                                            {processingStep >= 3 && (
                                                                <motion.div 
                                                                    initial={{ scale: 0 }}
                                                                    animate={{ scale: 1 }}
                                                                    transition={{ delay: i * 0.2 + 0.4 }}
                                                                    className="absolute right-0 bottom-1"
                                                                >
                                                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                                                </motion.div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="pt-8 border-t border-slate-200 mt-8">
                                                <div className="flex justify-between items-end">
                                                    <div className="w-1/2">
                                                        <div className="border-b border-slate-300 h-8"></div>
                                                        <label className="text-[10px] text-slate-400 uppercase mt-1">Verified By</label>
                                                    </div>
                                                    <div className="text-xs text-slate-400">
                                                        {processingStep >= 3 && "Verified on " + activeForm.date}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right: Live Feed / AI Actions */}
                                <div className="block w-1/2 p-8 bg-slate-50 overflow-y-auto">
                                    <div className="flex items-center justify-between mb-6">
                                        <h4 className="text-xs font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2">
                                            <Brain className="w-4 h-4" /> Live Extraction
                                        </h4>
                                    </div>

                                    <div className="space-y-4">
                                        {/* Activity Log */}
                                        <AnimatePresence>
                                            {processingStep >= 1 && (
                                                <motion.div initial={{x: 20, opacity:0}} animate={{x:0, opacity:1}} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-start gap-3">
                                                    <div className="p-2 bg-slate-100 rounded-lg">
                                                        <ScanLine className="w-4 h-4 text-slate-600" />
                                                    </div>
                                                    <div>
                                                        <h5 className="font-bold text-slate-900 text-sm">Identifying Form Fields</h5>
                                                        <p className="text-xs text-slate-500">Mapped 12 fields from "Office_Breakdown_v2.pdf"</p>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        <AnimatePresence>
                                            {processingStep >= 2 && (
                                                <motion.div initial={{x: 20, opacity:0}} animate={{x:0, opacity:1}} className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-start gap-3">
                                                    <div className="p-2 bg-blue-100 rounded-lg">
                                                        <Search className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h5 className="font-bold text-slate-900 text-sm">Fetching Benefits</h5>
                                                        <p className="text-xs text-slate-500">Connected to {activeForm.insurance} Payer Portal</p>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>

                                        {/* Extracted Values Cards */}
                                        <div className="pt-4 space-y-2">
                                            {processingStep >= 3 && activeForm.fields.map((field, i) => (
                                                <motion.div 
                                                    key={i}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: i * 0.1 }}
                                                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-100 text-sm"
                                                >
                                                    <span className="text-slate-500">{field.label}</span>
                                                    <span className="font-bold text-slate-900">{field.value}</span>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </div>
  );
};

// Helper for the dynamic badge
const ProcessingStatusBadge = ({ step }) => {
    const statuses = [
        { text: "Reading Template...", color: "text-slate-500", bg: "bg-slate-100" },
        { text: "Scanning...", color: "text-blue-500", bg: "bg-blue-50" },
        { text: "Fetching Data...", color: "text-indigo-500", bg: "bg-indigo-50" },
        { text: "Filling Form...", color: "text-purple-500", bg: "bg-purple-50" },
        { text: "Saved to PMS", color: "text-green-600", bg: "bg-green-50" }
    ];
    // Cap step at 4
    const current = statuses[Math.min(step, 4)] || statuses[0];
    
    return (
        <Badge variant="secondary" className={`${current.bg} ${current.color} text-[10px] h-5 border-0 shadow-none`}>
             {step < 4 && step > 0 && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
             {step === 4 && <CheckCircle className="w-3 h-3 mr-1" />}
             {current.text}
        </Badge>
    );
};

// Mock Data
const mockForms = [
  {
    id: "form-001",
    templateName: "Breakdown Form",
    patient: "Sarah Miller",
    dob: "04/12/1985",
    insurance: "Delta Dental",
    date: "10/14/2024",
    appointmentTime: "Tomorrow, 9:00 AM",
    fields: [
        { label: "Annual Maximum", value: "$2,000.00" },
        { label: "Deductible (Ind/Fam)", value: "$50 / $150" },
        { label: "Deductible Remaining", value: "$50.00" },
        { label: "Preventive (Class I)", value: "100%" },
        { label: "Basic (Class II)", value: "80%" },
        { label: "Major (Class III)", value: "50%" },
        { label: "Orthodontics", value: "Not Covered" },
        { label: "Waiting Period", value: "None" }
    ]
  },
  {
    id: "form-002",
    templateName: "Endo Referral",
    patient: "James Wilson",
    dob: "11/23/1979",
    insurance: "MetLife",
    date: "10/14/2024",
    appointmentTime: "Today, 2:30 PM",
    fields: [
        { label: "Referral To", value: "Dr. Smith (Endo)" },
        { label: "Tooth #", value: "14" },
        { label: "Procedure", value: "RCT" },
        { label: "Benefits Remaining", value: "$1,250.00" },
        { label: "Endo Coverage", value: "80%" },
        { label: "Notes", value: "Pain to cold/hot" },
    ]
  }
];

export default BenefitFormsSlide;
